/**
 * `/messages` - WHAT READERS WROTE TO THE AUTHOR: the six `supporter-messages` operations.
 *
 * Register R-006 (lens findings C3-02, U1; owed since decision-log row 216 (3)). LOCKED D44 (row 53):
 * "v1 cabinet keeps a minimal supporter-messages view (read + reply ... capability must not be
 * lost)". The platform served all six operations and no screen called one, while the Overview
 * counted messages nobody could open.
 *
 * WHAT AN AUTHOR MAY DO HERE, AND NOTHING MORE - the permission matrix's three author rows plus the
 * reading aid, each an operation the contract declares:
 *   answer        once per message, ever, and never edited (`replyToSupporterMessage`, 201)
 *   take it down  the author's own answer, by an append-only event (`retractOwnReply`)
 *   hide / show   the author's own hide flag on a PUBLIC message (`hide`/`unhideSupporterMessage`);
 *                 the editors' hide is a separate flag this screen shows and never touches
 *   translate     a job, not a translation (`requestSupporterMessageTranslation`, 202)
 * There is no "make public" and nothing on the reader's side: no such operation exists.
 *
 * ★ THE READER IS NAMED BY THEIR LABEL, NEVER IDENTIFIED. A message carries no sender id or handle,
 * by design ("a fingerprint never recorded cannot be recovered, leaked or subpoenaed"), but it does
 * carry `supporterName`, the byline name the reader chose (or the neutral reader label): the owner,
 * 2026-10-20 - an author thanking somebody for a tip must know whom they are writing to. The name
 * heads the card and stands beside the tip. And the article it was left on is one line - a small
 * picture, the title linking to the live page (`ArticleLine`).
 *
 * ★ NO OPTIMISM: THE PLATFORM'S ANSWER REPLACES THE CARD. Every act waits for it, and four of
 * them answer with the message (or its reply) as it now stands, so that answer replaces the card -
 * it is the server's word, not a guess. A 409 or 404 means the screen was behind: the list is read
 * again (./message-words.ts). "One answer ever" is the 409 the composer shows, never retries.
 *
 * ★ THE TRANSLATION ARRIVES LATER. The request enqueues and answers `queued`; the text is read back
 * from the message "on the next list read" - there is no poll route - so the card says it was asked
 * for and offers "Check again", which reads the list. `produced` (asked before) reads it at once.
 * The language is the cabinet's display language - the one the author is reading the cabinet in
 * (design 29, section 1b) - not the profile's `languageCode`, which is what MoonX writes to them in.
 *
 * DESIGN 15 (the owner's live look at real cabinets, 2026-10-01): the same screen in the cabinet's
 * new language - the filter as pills, each message a card with a round mark for how it was sent,
 * quiet meta lines, the author's answer on the gold wash /comments gives the author's own words,
 * and the acts in one row at the foot (./messages.module.css). Every act, refusal and state above is
 * unchanged. The mark is how the message was sent, never who sent it: there is no picture to draw.
 */
import { Alert, Button, Group, Stack, Text } from '@mantine/core';
import { useCallback, useState } from 'react';
import { Coins, Globe, Lock } from '@moonx/ui';
import { useCabinetApi } from '../../features/cabinet/api';
import type {
  PublicId,
  SupporterMessage,
  SupporterMessagePage,
  SupporterMessageReply,
} from '../../features/cabinet/api';
import {
  ArticleLine,
  CabinetPageHeader,
  EmptyState,
  ErrorState,
  LoadMore,
  LoadingState,
  Money,
  PillChoice,
  RefreshingNote,
} from '../../features/cabinet/components';
import { TextareaWithEmoji } from '../../features/cabinet/components/QuickEmoji';
import { useAction, useKeysetList } from '../../features/cabinet/state';
import { formatInstant } from '../../features/cabinet/format';
import { Trans, i18n, useTranslation } from '../../i18n';
import {
  actFailed,
  describeActFailure,
  isStaleRefusal,
  languageName,
  tipStatusWords,
} from './message-words';
import type { MessageAct } from './message-words';
import classes from './messages.module.css';

/** Where the screen lives. The Overview's supporter-messages tile links here. */
export const MESSAGES_PATH = '/messages';
type Filter = 'all' | 'public' | 'private';

export function MessagesRoute() {
  const { t } = useTranslation('engagement');
  const api = useCabinetApi();
  const [filter, setFilter] = useState<Filter>('all');
  // The platform's own answer to an act, by message id, laid over the list until the next read.
  const [answered, setAnswered] = useState<Readonly<Record<string, SupporterMessage>>>({});
  // Messages whose translation was asked for and came back `queued`.
  const [asked, setAsked] = useState<ReadonlySet<string>>(() => new Set());
  const [acting, setActing] = useState<{ readonly act: MessageAct; readonly id: string } | null>(
    null,
  );

  const read = useCallback(
    (cursor: string | undefined, signal: AbortSignal) =>
      api.listSupporterMessages(
        {
          ...(filter === 'all' ? {} : { visibility: filter }),
          ...(cursor === undefined ? {} : { cursor }),
        },
        signal,
      ),
    [api, filter],
  );
  const list = useKeysetList<SupporterMessage, SupporterMessagePage>(read, `messages:${filter}`);
  const reload = list.reload;

  // The language the author is reading the cabinet in; `useTranslation` redraws on a change.
  const target = i18n.language;

  /** A fresh read of the whole list: what the platform holds now replaces every laid-over answer. */
  const reread = useCallback(() => {
    setAnswered({});
    reload();
  }, [reload]);

  const action = useAction((work: () => Promise<true>) => work());
  const runAction = action.run;

  /** One act at a time. Resolves `true` once the platform accepted it, `false` when it refused. */
  const run = useCallback(
    async (act: MessageAct, id: PublicId, work: () => Promise<void>): Promise<boolean> => {
      setActing({ act, id });
      const done = await runAction(async () => {
        try {
          await work();
          return true as const;
        } catch (error) {
          if (isStaleRefusal(error)) {
            reread();
          }
          throw error;
        }
      });
      return done === true;
    },
    [runAction, reread],
  );

  // An act answers with the message as it now stands but without its article line (only a list read
  // joins one), so each act below lays its answer over the card it acted on.
  const lay = useCallback((message: SupporterMessage) => {
    setAnswered((current) => ({ ...current, [message.supporterMessageId]: message }));
  }, []);

  const handlers: CardHandlers = {
    reply: (message, body) =>
      run('reply', message.supporterMessageId, async () => {
        const reply = await api.replyToSupporterMessage(message.supporterMessageId, { body });
        lay({ ...message, reply });
      }),
    retract: (message) =>
      run('retract', message.supporterMessageId, async () => {
        lay({ ...message, ...(await api.retractOwnReply(message.supporterMessageId)) });
      }),
    hide: (message) =>
      run('hide', message.supporterMessageId, async () => {
        lay({ ...message, ...(await api.hideSupporterMessage(message.supporterMessageId)) });
      }),
    unhide: (message) =>
      run('unhide', message.supporterMessageId, async () => {
        lay({ ...message, ...(await api.unhideSupporterMessage(message.supporterMessageId)) });
      }),
    translate: (message, targetLanguage) =>
      run('translate', message.supporterMessageId, async () => {
        const state = await api.requestSupporterMessageTranslation(message.supporterMessageId, {
          targetLanguage,
        });
        if (state.state === 'produced') {
          reread();
          return;
        }
        setAsked((current) => new Set([...current, message.supporterMessageId]));
      }),
    checkAgain: reread,
  };

  const total = list.lastPage?.total;

  return (
    <Stack gap="lg">
      <CabinetPageHeader title={t('messages.title')} lede={t('messages.lede')} />

      <div className={classes.filters}>
        <PillChoice<Filter>
          label={t('messages.filter.label')}
          value={filter}
          onChange={setFilter}
          options={[
            { value: 'all', label: t('messages.filter.all') },
            { value: 'public', label: t('messages.filter.public') },
            { value: 'private', label: t('messages.filter.private') },
          ]}
        />
        {total === undefined || list.items.length === 0 ? null : (
          <span className={classes.total}>
            {total === 1 ? t('messages.total.one') : t('messages.total.many', { count: total })}
          </span>
        )}
      </div>

      {action.error === undefined || acting === null ? null : (
        <Alert
          variant="light"
          role="alert"
          title={actFailed(acting.act)}
          withCloseButton
          closeButtonLabel={t('messages.dismiss')}
          onClose={action.clearError}
          styles={{
            root: {
              backgroundColor: 'oklch(var(--mx-danger) / 0.10)',
              border: '1px solid oklch(var(--mx-danger) / 0.45)',
            },
            title: { color: 'oklch(var(--mx-danger))' },
          }}
        >
          <Text size="sm">{describeActFailure(acting.act, action.error)}</Text>
        </Alert>
      )}

      {list.isInitialLoad ? (
        <LoadingState label={t('messages.reading')} rows={3} />
      ) : list.status === 'error' && list.items.length === 0 ? (
        <ErrorState title={t('messages.loadFailed')} error={list.error} onRetry={reread} />
      ) : list.items.length === 0 ? (
        <EmptyState
          headline={t(`messages.empty.${filter}`)}
          explanation={t('messages.empty.explanation')}
        />
      ) : (
        <Stack gap="md">
          {list.status === 'error' ? (
            <ErrorState title={t('messages.lastReadFailed')} error={list.error} onRetry={reread} />
          ) : null}
          {list.isLoadingMore ? <RefreshingNote label={t('messages.loadingMore')} /> : null}

          {list.items.map((listed) => {
            const message = answered[listed.supporterMessageId] ?? listed;
            const busyHere =
              action.isRunning && acting !== null && acting.id === message.supporterMessageId;
            return (
              <MessageCard
                key={message.supporterMessageId}
                message={message}
                target={target}
                asked={asked.has(message.supporterMessageId)}
                busyAct={busyHere ? acting.act : null}
                locked={action.isRunning}
                handlers={handlers}
              />
            );
          })}

          <LoadMore
            hasMore={list.hasMore}
            isLoading={list.isLoadingMore}
            noun={t('messages.noun')}
            count={list.items.length}
            onLoadMore={list.loadMore}
          />
        </Stack>
      )}
    </Stack>
  );
}

interface CardHandlers {
  readonly reply: (message: SupporterMessage, body: string) => Promise<boolean>;
  readonly retract: (message: SupporterMessage) => Promise<boolean>;
  readonly hide: (message: SupporterMessage) => Promise<boolean>;
  readonly unhide: (message: SupporterMessage) => Promise<boolean>;
  readonly translate: (message: SupporterMessage, targetLanguage: string) => Promise<boolean>;
  readonly checkAgain: () => void;
}

interface MessageCardProps {
  readonly message: SupporterMessage;
  /** The language a translation is asked in: the cabinet's display language. */
  readonly target: string;
  readonly asked: boolean;
  /** The act running on THIS message, if any. */
  readonly busyAct: MessageAct | null;
  /** An act is running on some message: one at a time, so every control waits. */
  readonly locked: boolean;
  readonly handlers: CardHandlers;
}

/** Where the message shows, in words - four independent flags, read as one sentence. */
function whereItShows(visibility: SupporterMessage['visibility']): string {
  if (visibility.isRetracted) {
    return i18n.t('engagement:messages.where.retracted');
  }
  if (!visibility.isPublic) {
    return i18n.t('engagement:messages.where.private');
  }
  if (visibility.hiddenByAdmin) {
    return i18n.t('engagement:messages.where.hiddenByEditors');
  }
  if (visibility.hiddenByAuthor) {
    return i18n.t('engagement:messages.where.hiddenByYou');
  }
  return i18n.t('engagement:messages.where.shown');
}

function MessageCard({ message, target, asked, busyAct, locked, handlers }: MessageCardProps) {
  const { t } = useTranslation('engagement');
  const [composing, setComposing] = useState(false);
  const [draft, setDraft] = useState('');
  const visibility = message.visibility;
  const reply = message.reply ?? null;
  const translations = message.translations ?? [];
  const translated = translations.find((translation) => translation.targetLanguage === target);

  // What the foot row offers - the same four conditions the card always had, named so the row is
  // drawn only when it holds something.
  const canAnswer = reply === null && !visibility.isRetracted;
  const offersAnswer = canAnswer && !composing;
  const hideAct = visibility.hiddenByAuthor
    ? 'unhide'
    : visibility.isPublic && !visibility.isRetracted
      ? 'hide'
      : null;
  const translateAct = translated !== undefined ? null : asked ? 'asked' : 'offer';

  return (
    <article
      className={classes.card}
      aria-label={t('messages.card.label', {
        name: message.supporterName,
        when: formatInstant(message.postedAt),
      })}
    >
      <div className={classes.head}>
        <span className={classes.mark} aria-hidden="true">
          {message.initialPublic ? <Globe size={14} /> : <Lock size={14} />}
        </span>
        <div className={classes.headText}>
          <div className={classes.byline}>
            <span className={classes.who}>{message.supporterName}</span>
            <span className={classes.how}>
              {message.initialPublic
                ? t('messages.card.sentPublicly')
                : t('messages.card.sentPrivately')}
            </span>
            <span className={classes.when}>{formatInstant(message.postedAt)}</span>
            {visibility.hiddenByAuthor ? (
              <span className={classes.flag}>{t('messages.card.hiddenByYou')}</span>
            ) : null}
            {visibility.hiddenByAdmin ? (
              <span className={classes.flag}>{t('messages.card.hiddenByEditors')}</span>
            ) : null}
            {visibility.isRetracted ? (
              <span className={classes.flag}>{t('messages.card.takenBack')}</span>
            ) : null}
          </div>
          <div className={classes.where}>{whereItShows(visibility)}</div>
        </div>
      </div>

      <div className={classes.content}>
        {message.articleTitle === undefined ? null : (
          <ArticleLine
            title={message.articleTitle}
            webPath={message.articleWebPath}
            coverUrl={message.articleCoverUrl}
          />
        )}

        <TipLine message={message} />

        <p className={classes.body}>{message.body}</p>

        {translations.map((translation) => (
          <div key={translation.targetLanguage} className={classes.translation}>
            <div className={classes.translationLabel}>
              {translation.sourceLanguage === undefined || translation.sourceLanguage === null
                ? t('messages.card.translationIn', {
                    language: languageName(translation.targetLanguage),
                  })
                : t('messages.card.translationInFrom', {
                    language: languageName(translation.targetLanguage),
                    source: languageName(translation.sourceLanguage),
                  })}
            </div>
            <p className={classes.body}>{translation.body}</p>
          </div>
        ))}

        {reply === null ? null : (
          <ReplyView
            message={message}
            reply={reply}
            busy={busyAct === 'retract'}
            locked={locked}
            onRetract={handlers.retract}
          />
        )}

        {canAnswer && composing ? (
          <Stack gap="xs">
            <TextareaWithEmoji
              label={t('messages.card.replyLabel')}
              description={t('messages.card.replyDescription')}
              value={draft}
              autoFocus
              minRows={3}
              onChange={(value) => {
                setDraft(value);
              }}
            />
            <Group justify="flex-end" gap="xs">
              <Button
                variant="subtle"
                size="compact-sm"
                disabled={busyAct === 'reply'}
                onClick={() => {
                  setDraft('');
                  setComposing(false);
                }}
              >
                {t('messages.card.cancel')}
              </Button>
              <Button
                size="compact-sm"
                loading={busyAct === 'reply'}
                disabled={draft.trim().length === 0 || (locked && busyAct !== 'reply')}
                onClick={() => {
                  void handlers.reply(message, draft.trim()).then((sent) => {
                    // A refusal keeps the draft, so nothing typed is lost.
                    if (sent) {
                      setDraft('');
                      setComposing(false);
                    }
                  });
                }}
              >
                {t('messages.card.send')}
              </Button>
            </Group>
          </Stack>
        ) : null}
      </div>

      {!offersAnswer && hideAct === null && translateAct === null ? null : (
        <div className={classes.actions}>
          {offersAnswer ? (
            <Button
              variant="light"
              size="compact-sm"
              disabled={locked}
              onClick={() => {
                setComposing(true);
              }}
            >
              {t('messages.card.reply')}
            </Button>
          ) : null}
          {hideAct === 'unhide' ? (
            <Button
              variant="default"
              size="compact-sm"
              loading={busyAct === 'unhide'}
              disabled={locked && busyAct !== 'unhide'}
              onClick={() => {
                void handlers.unhide(message);
              }}
            >
              {t('messages.card.unhide')}
            </Button>
          ) : hideAct === 'hide' ? (
            <Button
              variant="default"
              size="compact-sm"
              loading={busyAct === 'hide'}
              disabled={locked && busyAct !== 'hide'}
              onClick={() => {
                void handlers.hide(message);
              }}
            >
              {t('messages.card.hide')}
            </Button>
          ) : null}
          {translateAct === null ? null : translateAct === 'asked' ? (
            <Group gap="xs">
              <Text size="xs" c="dimmed" role="status">
                {t('messages.card.translationAsked', { language: languageName(target) })}
              </Text>
              <Button variant="subtle" size="compact-sm" onClick={handlers.checkAgain}>
                {t('messages.card.checkAgain')}
              </Button>
            </Group>
          ) : (
            <Button
              variant="subtle"
              size="compact-sm"
              loading={busyAct === 'translate'}
              disabled={locked && busyAct !== 'translate'}
              onClick={() => {
                void handlers.translate(message, target);
              }}
            >
              {t('messages.card.translate', { language: languageName(target) })}
            </Button>
          )}
        </div>
      )}
    </article>
  );
}

/**
 * The tip that came with it, from whom, with its claim state - a fact from the ledger, never an
 * amount stored here. The name stands beside the amount, where the author looks to say thank you.
 */
function TipLine({ message }: { readonly message: SupporterMessage }) {
  const { t } = useTranslation('engagement');
  const status = message.tipStatus ?? null;
  const amount = message.tipAmountMinor ?? null;
  const currency = message.tipCurrency ?? null;
  if (status === null && amount === null) {
    return null;
  }
  return (
    <div className={classes.tip}>
      <Coins size={14} className={classes.tipMark} aria-hidden="true" />
      <span>
        {/* Who sent it stands beside the amount, where the author looks to say thank you. */}
        {amount === null || currency === null ? (
          <Trans
            t={t}
            i18nKey={status === null ? 'messages.tip.plain' : 'messages.tip.plainWithStatus'}
            values={status === null ? {} : { status: tipStatusWords(status) }}
            components={{ name: <SenderName name={message.supporterName} /> }}
          />
        ) : (
          <Trans
            t={t}
            i18nKey={status === null ? 'messages.tip.amount' : 'messages.tip.amountWithStatus'}
            values={status === null ? {} : { status: tipStatusWords(status) }}
            components={{
              money: <Money minor={amount} currency={currency} />,
              name: <SenderName name={message.supporterName} />,
            }}
          />
        )}
      </span>
    </div>
  );
}

/** The sender's label inside the tip line's sentence, set in bold. */
function SenderName({ name }: { readonly name: string }) {
  return <span className={classes.tipFrom}>{name}</span>;
}

/**
 * The answer's gold wash and the gold that reads on it, as /comments draws the author's own words -
 * inline, so a spec reads back what was drawn (test/routes/messages/messages.spec.tsx).
 */
const ANSWER_WASH = { backgroundColor: 'oklch(var(--mx-accent) / 0.07)' } as const;
const ANSWER_INK = { color: 'oklch(var(--mx-accent-hover))' } as const;

interface ReplyViewProps {
  readonly message: SupporterMessage;
  readonly reply: SupporterMessageReply;
  readonly busy: boolean;
  readonly locked: boolean;
  readonly onRetract: (message: SupporterMessage) => Promise<boolean>;
}

/**
 * The author's one answer. Taking it down is an EVENT: the words stay in the record, readers stop
 * seeing them, and no second answer can be written - so it asks once, in place, before it goes.
 */
function ReplyView({ message, reply, busy, locked, onRetract }: ReplyViewProps) {
  const { t } = useTranslation('engagement');
  const [confirming, setConfirming] = useState(false);

  return (
    <div className={classes.answer} style={ANSWER_WASH}>
      <div className={classes.answerHead} style={ANSWER_INK}>
        {t('messages.reply.head', { when: formatInstant(reply.postedAt) })}
      </div>
      <p className={classes.body}>{reply.body}</p>
      {reply.isRetracted ? (
        <div className={classes.quiet}>{t('messages.reply.takenDown')}</div>
      ) : reply.hiddenByAdmin === true ? (
        <div className={classes.quiet}>{t('messages.reply.hiddenByEditors')}</div>
      ) : confirming ? (
        <Stack gap="xs" mt={4}>
          <Text size="sm">{t('messages.reply.confirm')}</Text>
          <Group gap="xs">
            <Button
              variant="default"
              size="compact-sm"
              disabled={busy}
              onClick={() => {
                setConfirming(false);
              }}
            >
              {t('messages.reply.keep')}
            </Button>
            <Button
              size="compact-sm"
              loading={busy}
              disabled={locked && !busy}
              styles={{
                root: {
                  backgroundColor: 'oklch(var(--mx-danger))',
                  color: 'oklch(var(--mx-on-danger))',
                },
              }}
              onClick={() => {
                void onRetract(message).then(() => {
                  setConfirming(false);
                });
              }}
            >
              {t('messages.reply.takeDown')}
            </Button>
          </Group>
        </Stack>
      ) : (
        <Group>
          <Button
            variant="subtle"
            size="compact-xs"
            disabled={locked}
            onClick={() => {
              setConfirming(true);
            }}
          >
            {t('messages.reply.takeMineDown')}
          </Button>
        </Group>
      )}
    </div>
  );
}
